import { isNil } from "lodash"
import { useEffect, useRef } from "react"

import TextField from "@/components/form/textField/TextField"
import { PlayerMetric } from "@/types/metric"
import { CellParamsType } from "@/app/settings/metric/zones/components/type"
import {
  HR_BLOCKED_KEYS,
  isAllowedZoneCellInput,
} from "@/app/settings/metric/zones/components/validation"

export default function PlayerMaxCell({
  params,
  setPlayerMetrics,
  rowIndex,
  min,
  max,
  type,
  shouldFocus = false,
  onFocused,
}: {
  params: CellParamsType
  setPlayerMetrics: React.Dispatch<React.SetStateAction<PlayerMetric[]>>
  rowIndex: number
  min: number
  max: number
  type: string
  /** 같은 행의 활성 토글이 방금 On 이 됐다 — 이 칸으로 커서를 옮긴다 (ERR-020) */
  shouldFocus?: boolean
  onFocused?: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  // 토글이 On 이 되면 입력란이 활성화된 다음 렌더에서 포커스를 준다.
  // 한 번 옮기고 나면 요청을 지워, 이후 재렌더에서 커서를 다시 뺏지 않는다.
  useEffect(() => {
    if (!shouldFocus) {
      return
    }

    // type="number" 라 select() 는 먹지 않는다 — 포커스만 옮긴다
    inputRef.current?.focus()
    onFocused?.()
  }, [shouldFocus, onFocused])

  // 심박수는 정수다. 나머지 탭(속도·가속·감속)은 기존대로 소수 한 자리 (버그 #7·#8 과 같은 규칙)
  const isHr = type === "hr"
  const fixedMax = (value: string | number) =>
    isHr ? Math.round(Number(value)) : Math.round(Number(value) * 10) / 10

  const isErrorInput =
    params.row.active &&
    (isNil(params.row.max) || fixedMax(params.row.max) < min || fixedMax(params.row.max) > max)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 방향키 이벤트가 DataGrid로 전파되지 않도록 막기
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.stopPropagation()
    }

    // HR 은 소수점·지수·부호 키 자체를 막는다
    if (isHr && HR_BLOCKED_KEYS.includes(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <div className="zone-tab-table-max-input">
      <TextField
        inputRef={inputRef}
        value={params.row.max ?? ""}
        onChange={(e) => {
          // 붙여넣기·IME 등 키 입력을 거치지 않고 들어온 소수도 반영하지 않는다
          if (isHr && !isAllowedZoneCellInput(type, e.target.value)) {
            return
          }

          setPlayerMetrics((prev) => {
            const newPlayerMetrics = [...prev]
            newPlayerMetrics[rowIndex].max = e.target.value

            return newPlayerMetrics
          })
        }}
        onBlur={(e) => {
          if (e.target.value !== "") {
            setPlayerMetrics((prev) => {
              const newPlayerMetrics = [...prev]
              newPlayerMetrics[rowIndex].max = fixedMax(e.target.value)

              return newPlayerMetrics
            })
          }
        }}
        onKeyDown={handleKeyDown}
        type="number"
        disabled={!params.row.active}
        error={isErrorInput}
      />
    </div>
  )
}
